import { INotificationGroup, INotificationItem } from "contracts/notifications";
import { CancellationToken } from "Global/Base/Promises/CancellationTokenSource";
/**
 * Funkce vyvolávaná při změně stavu některé skupiny notifikací
 * @includeToDoc
 */
export type NotificationListener = () => void;

/**
 * Třída umožňuje sledování, načítání a manipulaci s notifikacemi
 * @includeToDoc
 */
export interface INotificationManager {
   /**
     * Načte všechny notifikace určité skupiny
     * @param groupName Název skupiny notifikací
     * @param onlyUnread Zda se mají načítat pouze nepřečtené notifikace
     * @param startIndex Od jaké pozice se mají načítat data
     * @param itemsCount Kolik položek se má načíst
     * @param cancellationToken Umožňuje zrušit stávající akci
     */
    getNotificationItems(groupName: string, onlyUnread: boolean, startIndex: number, itemsCount: number, cancellationToken?: CancellationToken): Promise<readonly Readonly<INotificationItem>[]>;
    /**
     * Načte všechny dostupné typy skupin notifikací spolu s jejich aktuálním stavem (existuje/neexistuje nepřečtená notifikace ve skupině).
     * @param cancellationToken Umožňuje zrušit stávající akci
     * @returns Kolekce typů notifikací se stavem
     */
    getNotificationGroups(cancellationToken?: CancellationToken): Promise<readonly Readonly<INotificationGroup>[]>;
    /**
     * Označí notifikaci jako přečtenou nebo nepřečtenou
     * @param notificationId Identifikátor notifikace
     * @param markAsRead Zda se má označit jako přečtená.
     * @param cancellationToken Umožňuje zrušit stávající akci
     */
    setNotificationAsRead(notificationId: number, markAsRead: boolean, cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Označí všechny notifikace dané skupiny jako přečtené
     * @param notificationGroupId Identigikátor skupiny
     * @param cancellationToken Umožňuje zrušit stávající akci
     */
     setGroupNotificationsAsRead(notificationGroupId: string, cancellationToken?: CancellationToken): Promise<void>;
    /**
     * Registruje listener pro naslouchání změnám stavu skupin notifikací (existuje/neexistuje nepřečtená notifikace ve skupině).
     * @param listener Listener volaný při změně stavu některé skupiny notifikací.
     */
    addNotificationGroupListener(listener: NotificationListener): Promise<void>;
    /**
     * Odregistruje listener pro naslouchání změnám stavu skupin notifikací.
     * @param listener Listener volaný při změně stavu některé skupiny notifikací.
     */
    removeNotificationGroupListener(listener: NotificationListener): Promise<void>;
}

export class INotificationManager {
    private static _methods: (keyof INotificationManager)[] = [
        "getNotificationItems",
        "getNotificationGroups",
        "setNotificationAsRead",
        "setGroupNotificationsAsRead",
        "addNotificationGroupListener",
        "removeNotificationGroupListener",
    ];

    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._methods.some(value => typeof instance[value] !== "function");
    }
}
